import { Modal, Radio } from '@mantine/core';
import Select from '@xver/components/Atoms/Select/Select';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Endpoints } from '@xver/apis';
import type {
    ProjectAccessLevel,
    ProjectAccessMap,
    ProjectMemberModel,
    ProjectModel,
    ProjectRole,
} from '@xver/apis/v1/projects/model';
import { Button, InputField } from '@xver/components/Atoms';
import { invalidateProjectQueries, useProjectFeedback } from '@xver/hooks/useProjectQueries';
import { getErrorCode } from '@xver/utils/error';
import {
    ACCESS_PRESETS,
    type AccessPreset,
    grantableRoles,
    memberLabel,
    presetFor,
} from '@xver/utils/projects';
import { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PRODUCTS } from '../../ProjectsPage/components/ProjectBits';

type GrantableRole = Exclude<ProjectRole, 'owner'>;

export type MemberAccessTarget = { kind: 'invite' } | { kind: 'edit'; member: ProjectMemberModel };

interface MemberAccessModalProps {
    project: ProjectModel;
    /** null = closed. */
    target: MemberAccessTarget | null;
    onClose: () => void;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const LEVELS: ProjectAccessLevel[] = ['none', 'view', 'manage'];
const NO_ACCESS: ProjectAccessMap = {
    vm: 'none',
    baremetal: 'none',
    container: 'none',
    block_storage: 'none',
};
const FULL_ACCESS: ProjectAccessMap = {
    vm: 'manage',
    baremetal: 'manage',
    container: 'manage',
    block_storage: 'manage',
};
const EMAIL_ERROR_CODES = new Set(['user_not_found', 'already_member', 'already_invited']);
const PRESET_OPTIONS = [...(Object.keys(ACCESS_PRESETS) as AccessPreset[]), 'custom'] as const;

export default function MemberAccessModal({ project, target, onClose }: MemberAccessModalProps) {
    const { t } = useTranslation('projects');
    const queryClient = useQueryClient();
    const { errorMessage, notifySuccess } = useProjectFeedback();
    const editing = target?.kind === 'edit' ? target.member : null;
    const roles = grantableRoles(project.role);
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<GrantableRole>('member');
    const [access, setAccess] = useState<ProjectAccessMap>(NO_ACCESS);
    const [emailError, setEmailError] = useState<string>();
    const emailRef = useRef<HTMLInputElement>(null);

    const { mutate, reset, isPending, error } = useMutation({
        mutationFn: async () => {
            const nextAccess = role === 'admin' ? FULL_ACCESS : access;
            if (editing) {
                return Endpoints.v1.Projects.updateMember(project.id, editing.userId, {
                    ...(role !== editing.role ? { role } : {}),
                    access: nextAccess,
                });
            }
            return Endpoints.v1.Projects.createInvitation(project.id, {
                email: email.trim(),
                role,
                access: nextAccess,
            });
        },
        onSuccess: async () => {
            notifySuccess(
                editing ? t('members.toast.updatedTitle') : t('members.toast.invitedTitle'),
                editing
                    ? t('members.toast.updatedMessage', { name: memberLabel(editing) })
                    : t('members.toast.invitedMessage', { email: email.trim() })
            );
            onClose();
            await invalidateProjectQueries(queryClient);
        },
        onError: (mutationError) => {
            if (EMAIL_ERROR_CODES.has(getErrorCode(mutationError) ?? '')) {
                setEmailError(errorMessage(mutationError));
                emailRef.current?.focus();
            }
        },
    });

    useEffect(() => {
        if (!target) return;
        reset();
        setEmailError(undefined);
        setEmail('');
        if (target.kind === 'edit') {
            setRole(target.member.role === 'admin' ? 'admin' : 'member');
            setAccess({ ...NO_ACCESS, ...target.member.access });
        } else {
            setRole('member');
            setAccess(NO_ACCESS);
        }
    }, [target, reset]);

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (isPending) return;
        if (!editing) {
            const trimmed = email.trim();
            const message = !trimmed
                ? t('invite.errors.emailRequired')
                : !EMAIL_PATTERN.test(trimmed)
                  ? t('invite.errors.emailInvalid')
                  : undefined;
            setEmailError(message);
            if (message) return emailRef.current?.focus();
        }
        mutate();
    };

    const handleClose = () => {
        if (!isPending) onClose();
    };

    const formError =
        error && !EMAIL_ERROR_CODES.has(getErrorCode(error) ?? '')
            ? errorMessage(error)
            : undefined;
    const preset = presetFor(access);
    const levelOptions = LEVELS.map((level) => ({
        value: level,
        label: t(`access.levels.${level}`),
    }));

    return (
        <Modal
            opened={!!target}
            onClose={handleClose}
            centered
            size="lg"
            closeOnClickOutside={!isPending}
            closeOnEscape={!isPending}
            closeButtonProps={{ 'aria-label': t('common.close'), disabled: isPending }}
            title={
                <span className="text-lg font-bold">
                    {editing
                        ? t('invite.editTitle', { name: memberLabel(editing) })
                        : t('invite.title', { name: project.name })}
                </span>
            }
        >
            <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-5">
                {editing ? (
                    editing.name &&
                    editing.email && <p className="text-sm text-fg/78">{editing.email}</p>
                ) : (
                    <InputField
                        ref={emailRef}
                        data-autofocus
                        type="email"
                        size="md"
                        label={t('invite.emailLabel')}
                        description={t('invite.emailDescription')}
                        value={email}
                        onChange={(event) => {
                            setEmail(event.target.value);
                            if (emailError) setEmailError(undefined);
                        }}
                        required
                        autoComplete="off"
                        readOnly={isPending}
                        error={emailError}
                    />
                )}

                {roles.length > 1 ? (
                    <Radio.Group
                        label={t('invite.roleLabel')}
                        value={role}
                        onChange={(value) => setRole(value as GrantableRole)}
                    >
                        <div className="mt-2 flex flex-col gap-3">
                            {roles.map((option) => (
                                <Radio
                                    key={option}
                                    value={option}
                                    label={t(`roles.${option}`)}
                                    description={t(`invite.roleHelp.${option}`)}
                                    disabled={isPending}
                                    size="sm"
                                    data-autofocus={(!!editing && option === role) || undefined}
                                />
                            ))}
                        </div>
                    </Radio.Group>
                ) : (
                    <p className="text-sm text-fg/78">{t('invite.memberOnly')}</p>
                )}

                {role === 'member' ? (
                    <fieldset className="flex flex-col gap-3">
                        <legend className="text-sm font-medium mb-1">
                            {t('invite.accessLegend')}
                        </legend>
                        <p className="text-sm text-fg/68">{t('invite.accessHelp')}</p>
                        <Radio.Group
                            label={t('invite.presets.label')}
                            description={t('invite.presets.help')}
                            value={preset}
                            onChange={(value) => {
                                if (value !== 'custom') {
                                    setAccess(ACCESS_PRESETS[value as AccessPreset]);
                                }
                            }}
                        >
                            <div className="mt-2 grid gap-3 sm:grid-cols-2">
                                {PRESET_OPTIONS.map((option) => (
                                    <Radio
                                        key={option}
                                        value={option}
                                        label={t(`invite.presets.${option}.label`)}
                                        description={t(`invite.presets.${option}.description`)}
                                        disabled={
                                            isPending ||
                                            (option === 'custom' && preset !== 'custom')
                                        }
                                        size="sm"
                                        data-autofocus={
                                            (!!editing && roles.length < 2 && option === preset) ||
                                            undefined
                                        }
                                    />
                                ))}
                            </div>
                        </Radio.Group>
                        {PRODUCTS.map((product) => (
                            <Select
                                key={product}
                                label={t(`products.${product}`)}
                                description={t(`invite.productHelp.${product}`)}
                                data={levelOptions}
                                value={access[product]}
                                onChange={(value) => {
                                    if (!value) return;
                                    const level = value as ProjectAccessLevel;
                                    setAccess((previous) => ({ ...previous, [product]: level }));
                                }}
                                allowDeselect={false}
                                disabled={isPending}
                                size="md"
                            />
                        ))}
                    </fieldset>
                ) : (
                    <p className="text-sm text-fg/78">{t('invite.adminAccess')}</p>
                )}

                {formError && (
                    <p role="alert" className="text-sm text-fg">
                        {formError}
                    </p>
                )}

                <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="secondary" onClick={handleClose} disabled={isPending}>
                        {t('common.cancel')}
                    </Button>
                    <Button type="submit" isLoading={isPending}>
                        {editing ? t('invite.save') : t('invite.submit')}
                    </Button>
                </div>
            </form>
        </Modal>
    );
}
